import { useEffect, useMemo, useRef } from 'react';

import {
  Sender,
  MessageType,
} from '@wix/ambassador-innovation-widget-v1-message/types';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import {
  addMessage,
  AddMessagePayload,
  getFaqQuestions,
  selectFaqQuestions,
  selectMessages,
  selectOneChatSettings,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import { useOneChatIntroData } from '@/hooks';
import { EXPERIMENTS } from '@/constants';

import { useAppExperiments } from 'AiAssistantWidget/experiments';

export const useOneChatIntroSequence = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { isEditor } = useEnvironment();
  const { experiments } = useAppExperiments();
  const shouldSkipLegalNotification = experiments.enabled(
    EXPERIMENTS.SKIP_LEGAL_NOTIFICATION,
  );
  const inFlightRef = useRef(false);
  // A failed send rolls back the optimistic intro, which makes the payload
  // derive again from history and would re-fire this effect endlessly (429s).
  // Attempt once per conversation generation instead.
  const attemptedGenerationRef = useRef<number | null>(null);
  const conversationGeneration = useAppSelector(
    selectMessages.conversationGeneration,
  );
  const {
    readiness,
    intro,
    legal,
    suggestions,
    faqQuestions,
    offline,
    contactForm,
    custom,
    isSequenceFulfilled,
    setIsSequenceFulfilled,
    addCustomMessage,
  } = useOneChatIntroData();

  const settingsLoaded = useAppSelector(selectOneChatSettings.isLoaded);
  const isManual = useAppSelector(selectOneChatSettings.isManual);
  const faqEnabled = useAppSelector(selectOneChatSettings.faqQuestionsEnabled);
  const shouldLoadFaqQuestions = useAppSelector(selectFaqQuestions.shouldLoad);

  useEffect(() => {
    if (
      !isEditor &&
      settingsLoaded &&
      isManual &&
      faqEnabled &&
      shouldLoadFaqQuestions
    ) {
      dispatch(getFaqQuestions());
    }
  }, [
    isEditor,
    settingsLoaded,
    isManual,
    faqEnabled,
    shouldLoadFaqQuestions,
    dispatch,
  ]);

  const payload = useMemo(() => {
    const result: AddMessagePayload[] = [];
    if (intro.shouldSendManualIntro) {
      result.push({
        body: { mainText: intro.manualMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.INTRO,
      });
    }
    if (intro.shouldSendAiIntro) {
      result.push({
        body: { mainText: intro.aiMessage as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.INTRO,
      });
    }
    if (legal.shouldSend) {
      result.push({
        body: { mainText: legal.message as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.LEGAL,
      });
      if (!shouldSkipLegalNotification) {
        result.push({
          body: { mainText: t('app.widget.chat.inboxLegalMessage') },
          sender: Sender.SYSTEM,
          messageType: MessageType.SITE_OWNER_NOTIFICATION,
        });
      }
    }
    if (suggestions.shouldSend) {
      const additionalData =
        suggestions.questions?.map(({ text: title }) => ({
          type: 'suggested_question',
          title,
        })) ?? [];
      const mainText = suggestions.introMessage || '';

      result.push({
        body: {
          mainText,
          additionalData,
        },
        sender: Sender.ASSISTANT,
        messageType: MessageType.SUGGESTED_QUESTION,
      });
    }

    if (offline.shouldSend) {
      result.push({
        body: { mainText: offline.message as string },
        sender: Sender.ASSISTANT,
        messageType: MessageType.OFFLINE,
      });
    }

    if (contactForm.shouldShowInIntroSequence) {
      result.push({
        body: {
          mainText: contactForm.introMessage ?? t('app.widget.form.beforeText'),
        },
        sender: Sender.ASSISTANT,
        messageType: MessageType.CONTACT_FORM,
      });
    }
    if (custom.shouldSend) {
      result.push({
        body: { mainText: custom.message },
      });
    }

    if (faqQuestions.shouldSend) {
      const additionalData = faqQuestions.questions.map(({ id, question }) => ({
        type: 'faq_question',
        id,
        title: question,
      }));
      result.push({
        body: {
          mainText: faqQuestions.introMessage ?? '',
          additionalData,
        },
        sender: Sender.ASSISTANT,
        messageType: MessageType.FAQ_QUESTION,
      });
    }

    return result;
  }, [
    intro,
    legal,
    suggestions,
    faqQuestions,
    offline,
    contactForm,
    custom,
    t,
    shouldSkipLegalNotification,
  ]);

  useEffect(() => {
    if (!readiness.loaded || !payload.length || isEditor) {
      return;
    }
    if (
      inFlightRef.current ||
      // Custom messages are user-triggered and cleared in `.finally`, so
      // they can't loop and must bypass the once-per-conversation guard.
      (!custom.shouldSend &&
        attemptedGenerationRef.current === conversationGeneration)
    ) {
      return;
    }
    inFlightRef.current = true;
    attemptedGenerationRef.current = conversationGeneration;
    setIsSequenceFulfilled(false);
    dispatch(addMessage(payload)).finally(() => {
      if (custom.shouldSend) {
        addCustomMessage('');
      }
      inFlightRef.current = false;
      setIsSequenceFulfilled(true);
    });
  }, [
    readiness.loaded,
    payload,
    dispatch,
    isEditor,
    conversationGeneration,
    custom.shouldSend,
    addCustomMessage,
    setIsSequenceFulfilled,
  ]);

  return {
    isSequenceFulfilled,
  };
};
